// src/firebase.js (100% JS)
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  enableIndexedDbPersistence,
  CACHE_SIZE_UNLIMITED,
  setLogLevel,
  collection,
  doc,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import firebase from "firebase/compat/app";

/* -------------------- ENV helpers (JS puro) -------------------- */
const toBool = (v, def) => {
  if (v == null) return !!def;
  const s = String(v).trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(s)) return true;
  if (["false", "0", "no", "n", "off"].includes(s)) return false;
  return !!def;
};

const FORCE_LONG_POLLING = toBool(
  process.env.REACT_APP_FIRESTORE_FORCE_LONG_POLLING,
  false
);
const USE_FETCH_STREAMS = toBool(
  process.env.REACT_APP_FIRESTORE_USE_FETCH_STREAMS,
  true
);
const CACHE_UNLIMITED = toBool(
  process.env.REACT_APP_FIRESTORE_CACHE_UNLIMITED,
  false
);
const RAW_LOG_LEVEL = (process.env.REACT_APP_FIRESTORE_LOG_LEVEL || "error")
  .toString()
  .trim();
const LOG_LEVEL = ["debug", "error", "silent"].includes(RAW_LOG_LEVEL)
  ? RAW_LOG_LEVEL
  : "error";

/* -------------------- Config Firebase -------------------- */
const firebaseConfigPro = {
  apiKey: process.env.REACT_APP_BICCIXPRESS_API_KEY,
  authDomain: process.env.REACT_APP_BICCIXPRESS_AUTHDOMAIN,
  databaseURL: process.env.REACT_APP_BICCIXPRESS_DATABASEURL,
  projectId: process.env.REACT_APP_BICCIXPRESS_PROYECID,
  storageBucket: process.env.REACT_APP_BICCIXPRESS_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_BICCIXPRESS_MESSAGINGSENDERIR,
  appId: process.env.REACT_APP_BICCIXPRESS_APPID,
  measurementId: process.env.REACT_APP_BICCIXPRESS_MEASUREMENTID,
};

if (!firebaseConfigPro?.apiKey || !firebaseConfigPro?.projectId) {
  // No rompas la app, pero deja claro en consola.
  // console.warn(
  //   "[Firebase] Faltan variables de entorno críticas (apiKey/projectId). Revisa tu .env"
  // );
}

const app = initializeApp(firebaseConfigPro);

/* -------------------- Firestore Init -------------------- */
setLogLevel(LOG_LEVEL);

// console.info("[Firestore Init] options", {
//   FORCE_LONG_POLLING,
//   USE_FETCH_STREAMS,
//   CACHE_UNLIMITED,
//   LOG_LEVEL,
// });

let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
      cacheSizeBytes: CACHE_UNLIMITED ? CACHE_SIZE_UNLIMITED : undefined,
    }),
    experimentalForceLongPolling: FORCE_LONG_POLLING,
    useFetchStreams: USE_FETCH_STREAMS,
  });

  // console.info(
  //   `[Firestore Init] Transport => ${
  //     FORCE_LONG_POLLING
  //       ? "LongPolling"
  //       : USE_FETCH_STREAMS
  //       ? "FetchStreams"
  //       : "WebChannel (default)"
  //   }`
  // );
} catch (e) {
  console.warn("initializeFirestore fallback -> enableIndexedDbPersistence", e);
  db = initializeFirestore(app, {}); // crea instancia
  enableIndexedDbPersistence(db).catch((err) => {
    console.warn("IndexedDB persistence failed:", err);
  });
}

/* -------------------- Otros servicios -------------------- */
const auth = getAuth(app);
const storage = getStorage(app);
const bucketName = `${firebaseConfigPro?.projectId || "unknown"}.appspot.com`;

/* -------------------- Probe de transporte -------------------- */
export function runFirestoreTransportProbe() {
  try {
    const testCol =
      process.env.REACT_APP_FIRESTORE_PROBE_COLLECTION || "healthchecks";
    const testDoc =
      process.env.REACT_APP_FIRESTORE_PROBE_DOC || "__transport_probe__";

    const ref = doc(collection(db, testCol), testDoc);

    // Fuerza red para validar transporte real
    getDoc(ref, { source: "server" })
      .then((snap) => {
        console.info("[Probe] getDoc(server) ok", {
          exists: snap.exists(),
          path: `${testCol}/${testDoc}`,
          fromCache: snap.metadata?.fromCache,
        });
      })
      .catch((err) => {
        console.error("[Probe] getDoc(server) ERROR", {
          code: err.code,
          message: err.message,
          name: err.name,
        });
      });

    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        console.info("[Probe] onSnapshot next", {
          exists: snap.exists(),
          hasPendingWrites: snap.metadata?.hasPendingWrites,
          fromCache: snap.metadata?.fromCache,
        });
      },
      (error) => {
        console.error("[Probe] onSnapshot ERROR", {
          name: error?.name,
          code: error?.code,
          message: error?.message,
        });
      }
    );

    // Auto-unsubscribe para no dejar listeners vivos
    const ms = Number(process.env.REACT_APP_FIRESTORE_PROBE_MS || 15000);
    setTimeout(() => {
      try {
        unsub();
        console.info("[Probe] onSnapshot unsubscribed");
      } catch {}
    }, ms);
  } catch (e) {
    console.error("[Probe] failed to start:", e);
  }
}

export { auth, firebase, db, storage, bucketName };
